/**
 * @fileoverview ConsoleSessionController - Vue-lifecycle-free Guacamole session.
 *
 * This module owns the heavy lifting of a Guacamole console session: tunnel
 * setup, client lifecycle, keyboard/mouse, paste, ResizeObserver, and the
 * exponential-backoff auto-reconnect loop. It is intentionally NOT bound to a
 * Vue component lifecycle so a single controller instance can outlive any
 * particular component (e.g. owned by a Pinia store for multi-session work).
 *
 * For backward compatibility, the existing `useConsole` composable in
 * `useConsole.js` wraps this factory and registers an `onUnmounted` disconnect
 * so the share portal continues to work unchanged.
 *
 * SECURITY MODEL (unchanged from original useConsole):
 * - Frontend NEVER receives VM credentials.
 * - Backend stores credentials encrypted (Fernet) in PostgreSQL.
 * - Backend issues a short-lived JWT containing re-encrypted credentials.
 * - Backend acts as a transparent proxy to guacd, intercepting `connect`.
 *
 * Usage:
 * ```
 * const controller = createConsoleSessionController({ maxReconnectAttempts: 5 })
 * controller.attach(displayElement)
 * await controller.connect(vmId)
 * // ...later...
 * controller.disconnect()
 * ```
 */

import { ref } from 'vue'
import { getConsoleToken, getShareConsoleToken } from '@/api/console'

/**
 * @typedef {'idle'|'connecting'|'connected'|'disconnected'|'reconnecting'|'error'} ConnectionState
 */

const debug = (message, data) => {
  if (import.meta.env.DEV) {
    if (data !== undefined) {
      console.debug(message, data)
    } else {
      console.debug(message)
    }
  }
}

const KEY_SYMBOLS = {
  CTRL: 0xffe3,
  ALT: 0xffe9,
  DELETE: 0xffff
}

/**
 * Create a ConsoleSessionController.
 *
 * Returns an object with reactive state refs and imperative methods. The
 * caller is responsible for invoking `disconnect()` when finished. No Vue
 * lifecycle hooks are registered here.
 *
 * @param {Object} [options]
 * @param {number} [options.maxReconnectAttempts=5]
 * @param {number} [options.initialReconnectDelay=1000]
 * @param {number} [options.maxReconnectDelay=30000]
 * @param {Function} [options.onClipboardReceived]
 * @returns {{
 *   connectionState: import('vue').Ref<ConnectionState>,
 *   errorMessage: import('vue').Ref<string|null>,
 *   reconnectAttempts: import('vue').Ref<number>,
 *   reconnectDelay: import('vue').Ref<number>,
 *   attach: (displayElement: HTMLElement) => void,
 *   connect: (vmId: string) => Promise<void>,
 *   connectShare: (shareId: string, vmId: string, sessionToken: string) => Promise<void>,
 *   disconnect: (shouldReconnect?: boolean) => void,
 *   sendClipboard: (text: string) => void,
 *   sendCtrlAltDel: () => void,
 *   setKeyboardEnabled: (enabled: boolean) => void,
 *   reconnect: () => void,
 *   scaleDisplayToFit: () => void
 * }}
 */
export function createConsoleSessionController(options = {}) {
  const {
    maxReconnectAttempts = 5,
    initialReconnectDelay = 1000,
    maxReconnectDelay = 30000,
    onClipboardReceived = null
  } = options

  const connectionState = ref('idle')
  const errorMessage = ref(null)
  const reconnectAttempts = ref(0)
  const reconnectDelay = ref(initialReconnectDelay)

  let client = null
  let tunnel = null
  let keyboard = null
  let keyboardEnabled = true
  let mouse = null
  let reconnectTimeoutId = null
  let connectionParams = null
  let resizeObserver = null

  function attach(displayElement) {
    debug('[ConsoleSession] attach()')

    if (!window.Guacamole) {
      console.error('[ConsoleSession] Guacamole library not found on window object')
      throw new Error(
        'Guacamole library not loaded. Ensure guacamole-common-js is included in index.html'
      )
    }

    if (!displayElement) {
      console.error('[ConsoleSession] Display element is required but not provided')
      throw new Error('Display element is required')
    }

    connectionParams = { displayElement }
    connectionState.value = 'idle'
    errorMessage.value = null
  }

  async function connect(vmId) {
    if (!connectionParams || !connectionParams.displayElement) {
      throw new Error('Client not initialized. Call attach() first.')
    }

    try {
      connectionParams.vmId = vmId
      connectionParams.connectionType = 'authenticated'

      debug('[ConsoleSession] connect()', vmId)
      connectionState.value = 'connecting'
      errorMessage.value = null

      const { ws_token, ws_url } = await getConsoleToken(vmId)
      const websocketUrl = `${ws_url}?token=${encodeURIComponent(ws_token)}`

      const displayElement = connectionParams.displayElement
      const rect = displayElement.getBoundingClientRect()
      if (rect.width === 0 || rect.height === 0) {
        throw new Error(
          'Display element has zero dimensions. Ensure the container is visible and sized before connecting.'
        )
      }

      _setupTunnel(websocketUrl)
      _setupClient(displayElement)
      client.connect('')
      _setupInputHandlers(displayElement)
    } catch (err) {
      errorMessage.value = err.message || 'Failed to connect'
      connectionState.value = 'error'
      throw err
    }
  }

  async function connectShare(shareId, vmId, shareSessionToken) {
    if (!connectionParams || !connectionParams.displayElement) {
      throw new Error('Client not initialized. Call attach() first.')
    }
    if (!shareSessionToken) {
      throw new Error('No share session token provided. User must be authenticated.')
    }

    try {
      connectionParams.shareId = shareId
      connectionParams.vmId = vmId
      connectionParams.shareSessionToken = shareSessionToken
      connectionParams.connectionType = 'share'

      debug('[ConsoleSession] connectShare()', { shareId, vmId })
      connectionState.value = 'connecting'
      errorMessage.value = null

      const { ws_token, ws_url } = await getShareConsoleToken(shareId, vmId, shareSessionToken)
      const websocketUrl = `${ws_url}?token=${encodeURIComponent(ws_token)}`

      const displayElement = connectionParams.displayElement
      const rect = displayElement.getBoundingClientRect()
      if (rect.width === 0 || rect.height === 0) {
        throw new Error(
          'Display element has zero dimensions. Ensure the container is visible and sized before connecting.'
        )
      }

      _setupTunnel(websocketUrl)
      _setupClient(displayElement)
      client.connect('')
      _setupInputHandlers(displayElement)
    } catch (err) {
      errorMessage.value = err.message || 'Failed to connect'
      connectionState.value = 'error'
      throw err
    }
  }

  function _setupTunnel(websocketUrl) {
    tunnel = new window.Guacamole.WebSocketTunnel(websocketUrl)

    const originalSendMessage = tunnel.sendMessage.bind(tunnel)
    tunnel.sendMessage = function (...args) {
      const opcode = args[0]
      if (opcode === 'size') {
        const MIN_DIMENSION = 100
        let width = parseInt(args[1], 10) || 1024
        let height = parseInt(args[2], 10) || 768
        if (width < MIN_DIMENSION || height < MIN_DIMENSION) {
          width = Math.max(MIN_DIMENSION, width) || 1024
          height = Math.max(MIN_DIMENSION, height) || 768
        }
        return originalSendMessage('size', width.toString(), height.toString())
      }
      return originalSendMessage.apply(this, args)
    }

    tunnel.onstatechange = state => {
      const stateNames = { 0: 'CONNECTING', 1: 'OPEN', 2: 'CLOSED' }
      debug('[ConsoleSession] Tunnel state', stateNames[state] || state)
    }

    tunnel.onerror = status => {
      console.error('[ConsoleSession] Tunnel error:', status)
      _handleError({
        message: `Tunnel error: ${status.message || 'Connection failed'}`,
        code: status.code
      })
    }
  }

  function _setupClient(displayElement) {
    client = new window.Guacamole.Client(tunnel)
    displayElement.appendChild(client.getDisplay().getElement())

    client.onstatechange = _handleStateChange
    client.onerror = _handleError
    client.onsync = () => {
      scaleDisplayToFit()
    }

    if (onClipboardReceived) {
      client.onclipboard = stream => {
        const reader = new window.Guacamole.StringReader(stream)
        let clipboardText = ''
        reader.ontext = text => {
          clipboardText += text
        }
        reader.onend = () => {
          onClipboardReceived(clipboardText)
        }
      }
    }
  }

  function _setupInputHandlers(displayElement) {
    keyboard = new window.Guacamole.Keyboard(document)

    let modifierPressed = false
    const KEYSYM_CTRL_L = 0xffe3
    const KEYSYM_CTRL_R = 0xffe4
    const KEYSYM_META_L = 0xffe7
    const KEYSYM_META_R = 0xffe8
    const KEYSYM_V_LOWER = 0x76
    const KEYSYM_V_UPPER = 0x56

    const isModifierKey = keysym =>
      keysym === KEYSYM_CTRL_L ||
      keysym === KEYSYM_CTRL_R ||
      keysym === KEYSYM_META_L ||
      keysym === KEYSYM_META_R

    keyboard.onkeydown = keysym => {
      if (client && keyboardEnabled) {
        if (isModifierKey(keysym)) {
          modifierPressed = true
        }
        if (modifierPressed && (keysym === KEYSYM_V_LOWER || keysym === KEYSYM_V_UPPER)) {
          navigator.clipboard
            .readText()
            .then(text => {
              if (text) {
                sendClipboard(text)
              }
            })
            .catch(err => console.warn('[ConsoleSession] clipboard read:', err.message))
          return false
        }
        client.sendKeyEvent(1, keysym)
        return false
      }
      return true
    }
    keyboard.onkeyup = keysym => {
      if (client && keyboardEnabled) {
        if (isModifierKey(keysym)) {
          modifierPressed = false
        }
        client.sendKeyEvent(0, keysym)
        return false
      }
      return true
    }

    const display = client.getDisplay()
    mouse = new window.Guacamole.Mouse(display.getElement())
    mouse.onmousedown =
      mouse.onmouseup =
      mouse.onmousemove =
        mouseState => {
          if (client) {
            client.sendMouseState(mouseState)
          }
        }

    const touch = new window.Guacamole.Mouse.Touchpad(display.getElement())
    touch.onmousedown =
      touch.onmouseup =
      touch.onmousemove =
        mouseState => {
          if (client) {
            client.sendMouseState(mouseState)
          }
        }

    const handlePaste = e => {
      if (client && connectionState.value === 'connected') {
        const text = e.clipboardData?.getData('text/plain')
        if (text) {
          sendClipboard(text)
        }
      }
    }
    document.addEventListener('paste', handlePaste)
    connectionParams.pasteHandler = handlePaste
  }

  function scaleDisplayToFit() {
    if (!client || !connectionParams || !connectionParams.displayElement) {
      return
    }

    try {
      const display = client.getDisplay()
      const displayElement = connectionParams.displayElement
      const displayWidth = display.getWidth()
      const displayHeight = display.getHeight()
      const containerRect = displayElement.getBoundingClientRect()
      const containerWidth = containerRect.width
      const containerHeight = containerRect.height

      const scaleX = containerWidth / displayWidth
      const scaleY = containerHeight / displayHeight
      const scale = Math.min(scaleX, scaleY, 1)

      display.scale(scale)
    } catch (err) {
      console.warn('[ConsoleSession] Failed to scale display:', err)
    }
  }

  function _setupResizeObserver() {
    if (!connectionParams || !connectionParams.displayElement) {
      return
    }

    if (resizeObserver) {
      resizeObserver.disconnect()
      resizeObserver = null
    }

    resizeObserver = new ResizeObserver(() => {
      scaleDisplayToFit()
    })
    resizeObserver.observe(connectionParams.displayElement)
  }

  function disconnect(shouldReconnect = false) {
    if (reconnectTimeoutId) {
      clearTimeout(reconnectTimeoutId)
      reconnectTimeoutId = null
    }

    if (resizeObserver) {
      resizeObserver.disconnect()
      resizeObserver = null
    }

    if (keyboard) {
      keyboard.onkeydown = null
      keyboard.onkeyup = null
      keyboard = null
    }

    if (mouse) {
      mouse.onmousedown = null
      mouse.onmouseup = null
      mouse.onmousemove = null
      mouse = null
    }

    if (connectionParams?.pasteHandler) {
      document.removeEventListener('paste', connectionParams.pasteHandler)
      connectionParams.pasteHandler = null
    }

    if (client) {
      const displayElement = connectionParams?.displayElement
      if (displayElement && client.getDisplay) {
        try {
          const display = client.getDisplay()
          if (display) {
            const guacDisplayElement = display.getElement()
            if (guacDisplayElement && displayElement.contains(guacDisplayElement)) {
              displayElement.removeChild(guacDisplayElement)
            }
          }
        } catch (err) {
          console.warn('[ConsoleSession] Error removing display:', err)
        }
      }

      try {
        client.disconnect()
      } catch (err) {
        console.warn('[ConsoleSession] Error disconnecting:', err)
      }
      client = null
    }

    if (tunnel) {
      tunnel.disconnect()
      tunnel = null
    }

    connectionState.value = 'disconnected'

    if (shouldReconnect && connectionParams && connectionParams.vmId) {
      _attemptReconnect()
    }
  }

  function sendClipboard(text) {
    if (client && connectionState.value === 'connected') {
      const stream = client.createClipboardStream('text/plain')
      const writer = new window.Guacamole.StringWriter(stream)
      writer.sendText(text)
      writer.sendEnd()
    }
  }

  function setKeyboardEnabled(enabled) {
    keyboardEnabled = enabled
  }

  function sendCtrlAltDel() {
    if (client && connectionState.value === 'connected') {
      client.sendKeyEvent(1, KEY_SYMBOLS.CTRL)
      client.sendKeyEvent(1, KEY_SYMBOLS.ALT)
      client.sendKeyEvent(1, KEY_SYMBOLS.DELETE)
      client.sendKeyEvent(0, KEY_SYMBOLS.DELETE)
      client.sendKeyEvent(0, KEY_SYMBOLS.ALT)
      client.sendKeyEvent(0, KEY_SYMBOLS.CTRL)
    }
  }

  function reconnect() {
    reconnectAttempts.value = 0
    reconnectDelay.value = initialReconnectDelay
    errorMessage.value = null
    if (connectionState.value !== 'disconnected') {
      disconnect(false)
    }
    if (connectionParams && connectionParams.vmId) {
      _attemptReconnect()
    }
  }

  function _attemptReconnect() {
    if (reconnectAttempts.value >= maxReconnectAttempts) {
      connectionState.value = 'error'
      errorMessage.value = `Failed to reconnect after ${maxReconnectAttempts} attempts`
      return
    }

    if (!connectionParams || !connectionParams.vmId || !connectionParams.connectionType) {
      connectionState.value = 'error'
      errorMessage.value = 'Cannot reconnect: missing connection parameters'
      return
    }

    connectionState.value = 'reconnecting'
    reconnectAttempts.value += 1

    reconnectTimeoutId = setTimeout(async () => {
      try {
        if (connectionParams.connectionType === 'authenticated') {
          await connect(connectionParams.vmId)
        } else if (connectionParams.connectionType === 'share') {
          await connectShare(
            connectionParams.shareId,
            connectionParams.vmId,
            connectionParams.shareSessionToken
          )
        } else {
          throw new Error(`Unknown connection type: ${connectionParams.connectionType}`)
        }
      } catch (err) {
        console.error('[ConsoleSession] Reconnect attempt failed:', err)
        _scheduleNextReconnect()
      }
    }, reconnectDelay.value)

    reconnectDelay.value = Math.min(reconnectDelay.value * 2, maxReconnectDelay)
  }

  function _scheduleNextReconnect() {
    if (reconnectAttempts.value < maxReconnectAttempts) {
      _attemptReconnect()
    } else {
      connectionState.value = 'error'
      errorMessage.value = `Failed to reconnect after ${maxReconnectAttempts} attempts`
    }
  }

  function _handleStateChange(state) {
    const Guacamole = window.Guacamole

    switch (state) {
      case Guacamole.Client.State.IDLE:
        connectionState.value = 'idle'
        break

      case Guacamole.Client.State.CONNECTING:
        connectionState.value = 'connecting'
        break

      case Guacamole.Client.State.WAITING:
        connectionState.value = 'connecting'
        if (client && connectionParams && connectionParams.displayElement) {
          const displayElement = connectionParams.displayElement
          const rect = displayElement.getBoundingClientRect()
          const MIN_WIDTH = 640
          const MIN_HEIGHT = 480
          const width = Math.max(MIN_WIDTH, Math.floor(rect.width) || 1024)
          const height = Math.max(MIN_HEIGHT, Math.floor(rect.height) || 768)
          if (tunnel && typeof tunnel.sendMessage === 'function') {
            tunnel.sendMessage('size', width.toString(), height.toString())
          }
        }
        break

      case Guacamole.Client.State.CONNECTED:
        connectionState.value = 'connected'
        errorMessage.value = null
        reconnectAttempts.value = 0
        reconnectDelay.value = initialReconnectDelay

        if (client && connectionParams && connectionParams.displayElement) {
          const displayElement = connectionParams.displayElement
          const rect = displayElement.getBoundingClientRect()
          const width = Math.floor(rect.width) || 1024
          const height = Math.floor(rect.height) || 768

          if (typeof client.sendSize === 'function') {
            client.sendSize(width, height)
          } else if (tunnel && typeof tunnel.sendMessage === 'function') {
            tunnel.sendMessage('size', width.toString(), height.toString())
          }

          scaleDisplayToFit()
          _setupResizeObserver()
        }
        break

      case Guacamole.Client.State.DISCONNECTING:
        connectionState.value = 'disconnected'
        break

      case Guacamole.Client.State.DISCONNECTED: {
        const wasConnected = connectionState.value === 'connected'
        const isReconnecting = connectionState.value === 'reconnecting'
        if (wasConnected && !isReconnecting) {
          disconnect(true)
        } else {
          connectionState.value = 'disconnected'
        }
        break
      }

      default:
        connectionState.value = 'idle'
    }
  }

  function _handleError(error) {
    errorMessage.value = error.message || 'Connection error'
    console.error('[ConsoleSession] Guacamole error:', error)
    disconnect(true)
  }

  return {
    connectionState,
    errorMessage,
    reconnectAttempts,
    reconnectDelay,
    attach,
    connect,
    connectShare,
    disconnect,
    sendClipboard,
    sendCtrlAltDel,
    setKeyboardEnabled,
    reconnect,
    scaleDisplayToFit
  }
}
